// services/Api.js
import axios from "axios";
import { notification } from "antd";
import { getAccessToken, setAccessToken } from "./tokenStore";

const BASE = process.env.REACT_APP_LIVE_URL;

const api = axios.create({
  baseURL: BASE,
  withCredentials: true,   // needed so the browser stores/sends the HttpOnly refresh cookie
  headers: { Accept: "*/*" },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// Lazy-load store to avoid circular dependency (store → authSlice → Api → store)
const getStore = () => require("../store").default;

const forceLogout = () => {
  setAccessToken(null);
  const { signOutSuccess } = require("store/slices/authSlice");
  getStore().dispatch(signOutSuccess());
  notification.warning({
    message: "Session expired",
    description: "Your session has expired. Please log in again.",
    duration: 5,
  });
};

// ── Request interceptor: attach current access token ────────
api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  if (!(config.data instanceof FormData)) {
    config.headers["Content-Type"] = "application/json";
  }
  return config;
});

// ── Response interceptor: silently refresh on 401 ───────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Queue concurrent requests that arrive while a refresh is in flight
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers["Authorization"] = "Bearer " + token;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    // Refresh token lives in an HttpOnly cookie — send no body, just credentials
    return axios
      .post(`${BASE}/token/refresh/`, {}, { withCredentials: true })
      .then(({ data }) => {
        setAccessToken(data.access);
        const { setToken } = require("store/slices/authSlice");
        getStore().dispatch(setToken(data.access));
        originalRequest.headers["Authorization"] = "Bearer " + data.access;
        processQueue(null, data.access);
        return api(originalRequest);
      })
      .catch((err) => {
        processQueue(err, null);
        forceLogout();
        return Promise.reject(err);
      })
      .finally(() => {
        isRefreshing = false;
      });
  }
);

export default function API(route, method, body, config = {}) {
  return api({
    method: method,
    url: route,
    responseType: "json",
    data: body,
    ...config,
  })
    .then((res) => ({
      status: "success",
      data: res.data,
    }))
    .catch((err) => ({
      status: "fail",
      data: err.response?.data || err.message,
      errorCode: err.status,
    }));
}

export const LoginApi = (data) => {
  return axios.post(BASE + "/login/", data);
};

export const API_URL = BASE + "/";
export const MEDIA_URL = BASE + "/media/images/";
export const AVATAR_URL = BASE + "/media/avatars/";
